import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TableHead, TableRow } from '@/components/ui/table';
// resources/js/components/table-components.tsx
import {
    Check,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    Columns3,
    Download,
    Filter,
    Printer,
    Search,
    X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
const baseUrl = import.meta.env?.VITE_APP_URL ?? (typeof window !== 'undefined' ? window.location.origin : '');

function debounce<T extends unknown[]>(fn: (...args: T) => void, wait = 300) {
    let timeout: ReturnType<typeof setTimeout> | null = null;

    const wrapped = (...args: T) => {
        if (timeout) {
            clearTimeout(timeout);
        }
        timeout = setTimeout(() => fn(...args), wait);
    };

    wrapped.cancel = () => {
        if (timeout) {
            clearTimeout(timeout);
            timeout = null;
        }
    };

    return wrapped;
}

// =====================
// Print Function
// =====================

interface PrintTableOptions {
    title: string;
    subtitle?: string;
    columns: Array<{ key: string; label: string }>;
    data: object[];
    visibleColumns?: string[];
    companyName?: string;
    companyAddress?: string;
    showLogo?: boolean;
    logoUrl?: string;
}

function printTable(options: PrintTableOptions) {
    const {
        title,
        subtitle = '',
        columns,
        data,
        visibleColumns,
        companyName = 'Your Company Name',
        companyAddress = 'Your Company Address',
        showLogo = true,
        logoUrl = `${baseUrl}/images/logo.webp`,
    } = options;

    const visibleCols = visibleColumns ? columns.filter((col) => visibleColumns.includes(col.key)) : columns;

    const currentDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });

    const tableHTML = `
        <table>
            <thead>
                <tr>
                    ${visibleCols.map((col) => `<th>${col.label}</th>`).join('')}
                </tr>
            </thead>
            <tbody>
                ${data
                    .map(
                        (row) => `
                    <tr>
                        ${visibleCols
                            .map(
                                (col) => `
                            <td>${(row as Record<string, unknown>)[col.key] !== undefined ? (row as Record<string, unknown>)[col.key] : ''}</td>
                        `,
                            )
                            .join('')}
                    </tr>
                `,
                    )
                    .join('')}
            </tbody>
        </table>
        
        <div style="margin-top: 20px; font-size: 12px; color: #6b7280;">
            <p><strong>Total Records:</strong> ${data.length}</p>
        </div>
    `;

    const html = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>${title}</title>
    <style>
        @media print {
            @page {
                size: A4 portrait;
                margin: 1cm;
            }
            body {
                margin: 0;
                padding: 0;
            }
            .no-print {
                display: none !important;
            }
        }
        
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            color: #1f2937;
            background: white;
            padding: 20px;
        }
        
        .print-header {
            border-bottom: 3px solid #0A7A35;
            padding-bottom: 20px;
            margin-bottom: 30px;
        }
        
        .print-header-top {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 15px;
        }
        
        .print-logo {
            max-width: 150px;
            max-height: 60px;
        }
        
        .print-company-info {
            text-align: right;
        }
        
        .print-company-name {
            font-size: 18px;
            font-weight: 700;
            color: #111827;
            margin-bottom: 5px;
        }
        
        .print-company-address {
            font-size: 12px;
            color: #6b7280;
            line-height: 1.5;
        }
        
        .print-title {
            font-size: 24px;
            font-weight: 700;
            color: #111827;
            margin-bottom: 5px;
        }
        
        .print-subtitle {
            font-size: 14px;
            color: #6b7280;
        }
        
        .print-date {
            font-size: 12px;
            color: #6b7280;
            margin-top: 10px;
        }
        
        .print-content {
            margin-top: 20px;
        }
        
        .print-footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #e5e7eb;
            font-size: 11px;
            color: #9ca3af;
            text-align: center;
        }
        
        table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
            font-size: 13px;
        }
        
        th {
            background: #f3f4f6;
            color: #374151;
            font-weight: 600;
            padding: 12px;
            text-align: left;
            border-bottom: 2px solid #d1d5db;
        }
        
        td {
            padding: 10px 12px;
            border-bottom: 1px solid #e5e7eb;
        }
        
        tr:nth-child(even) {
            background: #f9fafb;
        }
        
        .print-button-container {
            position: fixed;
            top: 20px;
            right: 20px;
            z-index: 1000;
        }
        
        .print-button {
            background: #0A7A35;
            color: white;
            border: none;
            padding: 12px 24px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }
        
        .print-button:hover {
            background: #08632b;
        }
    </style>
</head>
<body>
    <div class="print-button-container no-print">
        <button class="print-button" onclick="window.print()">🖨️ Print Document</button>
    </div>
    
    <div class="print-header">
        <div class="print-header-top">
            ${showLogo ? `<img src="${logoUrl}" alt="Logo" class="print-logo" onerror="this.style.display='none'">` : '<div></div>'}
            <div class="print-company-info">
                <div class="print-company-name">${companyName}</div>
                <div class="print-company-address">${companyAddress}</div>
            </div>
        </div>
        <div>
            <h1 class="print-title">${title}</h1>
            ${subtitle ? `<p class="print-subtitle">${subtitle}</p>` : ''}
            <p class="print-date">Printed on: ${currentDate}</p>
        </div>
    </div>
    
    <div class="print-content">
        ${tableHTML}
    </div>
    
    <div class="print-footer">
        <p>© ${new Date().getFullYear()} ${companyName}. All rights reserved.</p>
        <p>This is a computer-generated document.</p>
    </div>
</body>
</html>
    `;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
        alert('Please allow popups for printing');
        return;
    }

    printWindow.document.write(html);
    printWindow.document.close();
}

// =====================
// Table Top Controls
// =====================

interface TableTopControlsProps {
    searchTerm: string;
    onSearchChange: (value: string) => void;
    visibleColumns: string[];
    onColumnToggle: (column: string) => void;
    columns: Array<{ key: string; label: string }>;
    onExport: () => void;
    selectedCount?: number;
    onBulkAction?: (action: string) => void;
    showFilters?: boolean;
    onToggleFilters?: (show: boolean) => void;
    // Print options
    printTitle?: string;
    printData?: object[];
    companyName?: string;
    companyAddress?: string;
}

export function TableTopControls({
    searchTerm,
    onSearchChange,
    visibleColumns,
    onColumnToggle,
    columns,
    onExport,
    selectedCount = 0,
    onBulkAction,
    showFilters = false,
    onToggleFilters,
    printTitle = 'Document',
    printData = [],
    companyName = 'Your Company Name',
    companyAddress = 'Your Company Address',
}: TableTopControlsProps) {
    const [localSearch, setLocalSearch] = useState(searchTerm);
    const [showColumns, setShowColumns] = useState(false);
    const [showActions, setShowActions] = useState(false);

    const columnsRef = useRef<HTMLDivElement>(null);
    const actionsRef = useRef<HTMLDivElement>(null);

    const debouncedSearch = useRef(
        debounce((value: string) => {
            onSearchChange(value);
        }, 300),
    ).current;

    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (columnsRef.current && !columnsRef.current.contains(event.target as Node)) {
                setShowColumns(false);
            }
            if (actionsRef.current && !actionsRef.current.contains(event.target as Node)) {
                setShowActions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSearchChange = (value: string) => {
        setLocalSearch(value);
        debouncedSearch(value);
    };

    const clearSearch = () => {
        setLocalSearch('');
        onSearchChange('');
    };

    const handlePrint = () => {
        printTable({
            title: printTitle,
            subtitle: `Total ${printData.length} record${printData.length !== 1 ? 's' : ''}`,
            columns,
            data: printData,
            visibleColumns,
            companyName,
            companyAddress,
        });
        setShowActions(false);
    };

    return (
        <div className="mb-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                {/* Search */}
                <div className="relative w-full min-w-48 flex-1 sm:max-w-md">
                    <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                    <Input
                        type="text"
                        placeholder="Search..."
                        value={localSearch}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        className="pr-9 pl-9"
                    />
                    {localSearch && (
                        <button
                            onClick={clearSearch}
                            aria-label="Clear search"
                            type="button"
                            className="text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    )}
                </div>

                {/* Right buttons */}
                <div className="flex flex-wrap items-center gap-2">
                    {/* Actions dropdown */}
                    <div className="relative" ref={actionsRef}>
                        <Button onClick={() => setShowActions(!showActions)} variant="outline" type="button">
                            <Download className="h-4 w-4" />
                            Actions
                            <ChevronDown className="h-4 w-4" />
                        </Button>

                        {showActions && (
                            <div className="bg-card border-border absolute right-0 z-50 mt-2 w-48 rounded-lg border py-1 shadow-lg">
                                <div className="text-muted-foreground px-3 py-2 text-xs font-semibold uppercase">Exports</div>
                                <button
                                    onClick={() => {
                                        onExport();
                                        setShowActions(false);
                                    }}
                                    className="text-foreground hover:bg-muted flex w-full items-center gap-2 px-3 py-2 text-left text-sm"
                                >
                                    <Download className="h-4 w-4" />
                                    CSV export
                                </button>
                                <button
                                    onClick={handlePrint}
                                    className="text-foreground hover:bg-muted flex w-full items-center gap-2 px-3 py-2 text-left text-sm"
                                >
                                    <Printer className="h-4 w-4" />
                                    Print
                                </button>

                                {selectedCount > 0 && onBulkAction && (
                                    <>
                                        <div className="border-border my-1 border-t"></div>
                                        <div className="text-muted-foreground px-3 py-2 text-xs font-semibold uppercase">Bulk Actions</div>
                                        <button
                                            onClick={() => {
                                                onBulkAction('delete');
                                                setShowActions(false);
                                            }}
                                            className="hover:bg-muted w-full px-3 py-2 text-left text-sm text-red-600 dark:text-red-400"
                                        >
                                            Delete
                                        </button>
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Filters toggle */}
                    <Button
                        onClick={() => onToggleFilters?.(!showFilters)}
                        variant="outline"
                        type="button"
                        aria-pressed={showFilters}
                        className={showFilters ? 'border-primary/20 bg-primary/5 text-primary' : undefined}
                    >
                        <Filter className="h-4 w-4" />
                        Filters
                    </Button>

                    {/* Columns */}
                    <div className="relative" ref={columnsRef}>
                        <Button onClick={() => setShowColumns(!showColumns)} variant="outline" type="button">
                            <Columns3 className="h-4 w-4" />
                            Columns
                        </Button>

                        {showColumns && (
                            <div className="bg-card border-border absolute right-0 z-50 mt-2 max-h-80 w-48 overflow-y-auto rounded-lg border py-1 shadow-lg">
                                <div className="text-muted-foreground border-border border-b px-3 py-2 text-xs font-semibold uppercase">
                                    Toggle columns
                                </div>
                                {columns.map((col) => (
                                    <button
                                        key={col.key}
                                        onClick={() => onColumnToggle(col.key)}
                                        className="text-foreground hover:bg-muted flex w-full flex-wrap items-center justify-between gap-3 px-3 py-2 text-left text-sm"
                                    >
                                        <span>{col.label}</span>
                                        {visibleColumns.includes(col.key) && <Check className="text-primary h-4 w-4" />}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

// =====================
// Filter Row (unchanged)
// =====================

interface TableFilterRowProps {
    showFilters: boolean;
    filters: Record<string, string>;
    columns: Array<{ key: string; label: string }>;
    visibleColumns: string[];
    onFilterChange: (column: string, value: string) => void;
    leadingColumns?: number;
    trailingColumns?: number;
    nonFilterableKeys?: string[];
}

export function TableFilterRow({
    showFilters,
    filters,
    columns,
    visibleColumns,
    onFilterChange,
    leadingColumns = 2,
    trailingColumns = 1,
    nonFilterableKeys = [],
}: TableFilterRowProps) {
    const [localFilters, setLocalFilters] = useState<Record<string, string>>(filters);

    const debouncedFilter = useRef(
        debounce((column: string, value: string) => {
            onFilterChange(column, value);
        }, 300),
    ).current;

    useEffect(() => {
        setLocalFilters(filters);
    }, [filters]);

    if (!showFilters) return null;

    const visibleColumnsList = columns.filter((col) => visibleColumns.includes(col.key));

    const handleInputChange = (column: string, value: string) => {
        setLocalFilters((prev) => ({ ...prev, [column]: value }));
        debouncedFilter(column, value);
    };

    const handleClear = (column: string) => {
        setLocalFilters((prev) => ({ ...prev, [column]: '' }));
        onFilterChange(column, '');
    };

    return (
        <TableRow className="bg-muted/30 border-border border-b">
            {Array.from({ length: leadingColumns }).map((_, index) => (
                <TableHead key={`leading-${index}`} className="px-4 py-3"></TableHead>
            ))}

            {visibleColumnsList.map((col) => {
                const filterValue = localFilters[col.key] || '';
                const isNonFilterable =
                    col.key === 'image' || col.key === 'pdf' || col.key === 'logo' || col.key === 'sdgs' || nonFilterableKeys.includes(col.key);

                return (
                    <TableHead key={col.key} className="px-4 py-3">
                        <div className="relative">
                            {!isNonFilterable && (
                                <>
                                    <Input
                                        type="text"
                                        value={filterValue}
                                        onChange={(e) => handleInputChange(col.key, e.target.value)}
                                        className="h-8 pr-8 font-normal normal-case"
                                    />
                                    {filterValue && (
                                        <button
                                            onClick={() => handleClear(col.key)}
                                            aria-label={`Clear ${col.label} filter`}
                                            type="button"
                                            className="text-muted-foreground hover:text-foreground absolute top-1/2 right-2 -translate-y-1/2 transition-colors"
                                        >
                                            <X className="h-4 w-4" />
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    </TableHead>
                );
            })}

            {Array.from({ length: trailingColumns }).map((_, index) => (
                <TableHead key={`trailing-${index}`} className="px-4 py-3"></TableHead>
            ))}
        </TableRow>
    );
}

// =====================
// Pagination (unchanged)
// =====================

interface TablePaginationProps {
    currentPage: number;
    lastPage: number;
    perPage: number;
    total: number;
    from: number;
    to: number;
    onPageChange: (page: number) => void;
    onPerPageChange: (perPage: number) => void;
    hidePerPage?: boolean;
}

export function TablePagination({
    currentPage,
    lastPage,
    perPage,
    total,
    from,
    to,
    onPageChange,
    onPerPageChange,
    hidePerPage = false,
}: TablePaginationProps) {
    if (total === 0) return null;

    return (
        <div className="border-border mt-4 border-t pt-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                {!hidePerPage && (
                    <div className="flex items-center gap-2">
                        <span className="text-muted-foreground text-sm">Rows per page</span>
                        <select
                            value={perPage}
                            aria-label="Rows per page"
                            onChange={(e) => onPerPageChange(parseInt(e.target.value))}
                            className="border-input bg-background focus:ring-ring h-9 rounded-md border px-3 text-sm focus:ring-2 focus:outline-none"
                        >
                            <option value={10}>10</option>
                            <option value={15}>15</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                )}

                <div className="text-muted-foreground text-sm">
                    Showing {from}-{to} of {total} | Page {currentPage} of {lastPage}
                </div>

                <div className="flex items-center gap-1">
                    <Button
                        onClick={() => onPageChange(1)}
                        disabled={currentPage <= 1}
                        variant="outline"
                        size="icon"
                        type="button"
                        className="h-8 w-8"
                        title="First page"
                    >
                        <ChevronsLeft className="h-4 w-4" />
                    </Button>
                    <Button
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={currentPage <= 1}
                        variant="outline"
                        size="icon"
                        type="button"
                        className="h-8 w-8"
                        title="Previous page"
                    >
                        <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={currentPage >= lastPage}
                        variant="outline"
                        size="icon"
                        type="button"
                        className="h-8 w-8"
                        title="Next page"
                    >
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button
                        onClick={() => onPageChange(lastPage)}
                        disabled={currentPage >= lastPage}
                        variant="outline"
                        size="icon"
                        type="button"
                        className="h-8 w-8"
                        title="Last page"
                    >
                        <ChevronsRight className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        </div>
    );
}
